import Button from "components/buttons";
import DynamicFormInput from "components/form/form";
import Notes from "components/notes";
import PageTitle from "components/pageTitle";
import TwoColumnLayout from "layouts/TwoColumnLayout";
import { ChangeEvent, useRef, useState } from "react";
import { useToast } from "contexts/ToastContext";
import { LoadingIcon } from "utils/constant";
import FileUpload from "components/FileUpload";
import {
  LEGISLATIVE_DRAFTING_REQUEST_STATUS,
  TokenPayload,
} from "utils/constantValues";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import {
  createLegalTranslation,
  updateLegalTranslation,
} from "services/legalTranslationService";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { expandRoles } from "utils/authRules";

const CreateLegalTranslation = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const contextData = useAuth();

  const location = useLocation();
  const isInternalMode = location.pathname.includes('/legal-translation/internal') || location.pathname.includes('/internal/request');
  const editData = location.state?.request;

  const [formState, setFormState] = useState({
    request_summary: editData?.request_summary || "",
    request_letter_reference_number: editData?.request_letter_reference_number || "",
    translation_request_document: null as File | null,
    applied_date: editData?.applied_date || new Date().toISOString().slice(0, 10),
    request_letter_files: null as File | null,
    request_documents: null as File | null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (isInternalMode) {
      // Internal mode validation
      if (!formState.translation_request_document) {
        newErrors.translation_request_document = t("SupportingDocumentsRequired") || "Supporting Documents for Translation is required";
      }
      if (!formState.request_letter_reference_number?.trim()) {
        newErrors.request_letter_reference_number = t("RequestLetterReferenceNumberRequired") || "Request Letter Reference Number is required";
      }
      if (!formState.request_summary?.trim()) {
        newErrors.request_summary = t("SummaryOfTranslationRequestRequired") || "Summary of Translation Request is required";
      }
      if (!formState.applied_date?.trim()) {
        newErrors.applied_date = t("AppliedDateRequired") || "Applied Date is required";
      }
    } else {
      // External mode validation
      if (!formState.request_letter_files) {
        newErrors.request_letter_files = t("OfficialRequestLetterRequired") || "Official Request Letter is required";
      }
      if (!formState.request_documents) {
        newErrors.request_documents = t("SupportingDocumentsRequired") || "Supporting Documents for Translation is required";
      }
      if (!formState.request_letter_reference_number?.trim()) {
        newErrors.request_letter_reference_number = t("RequestLetterReferenceNumberRequired") || "Request Letter Reference Number is required";
      }
      if (!formState.request_summary?.trim()) {
        newErrors.request_summary = t("SummaryOfTranslationRequestRequired") || "Summary of Translation Request is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearError = (field: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const fileInputRefTranslationDoc = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (
    file: File | null,
    fieldName: keyof Pick<typeof formState, "translation_request_document">
  ) => {
    setFormState((prev) => ({ ...prev, [fieldName]: file }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      showToast(t("PleaseFillAllRequiredFields") || "Please fill in all required fields", "error");
      return;
    }

    setSubmitting(true);

    const data = new FormData();
    if (isInternalMode) {
      if (formState.request_letter_reference_number) {
        data.append("request_letter_reference_number", formState.request_letter_reference_number);
      }
      if (formState.request_summary) {
        data.append("request_summary", formState.request_summary);
      }
      if (formState.translation_request_document) {
        data.append("request_documents", formState.translation_request_document);
      }
      if (formState.applied_date) {
        data.append("applied_date", formState.applied_date);
      }
      data.append("is_internal", "true");
      data.append("assigned_by", contextData.user?.full_name || contextData.user?.username || "");
      
      // Check if user is a Director or Coordinator
      const isDirector = contextData.user?.roles?.includes("LRDIFUDDirector");
      // LegalTranslationCordinator counts as a coordinator (see ROLE_ALIASES).
      const isCoordinator = expandRoles(contextData.user?.roles || []).includes("LegalResearchCoordinator");
      
      // Set status and forward_status for internal requests
      if (isDirector) {
        data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.head_remark_submitted.value);
        data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_director.value);
      } else if (isCoordinator) {
        data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.director_remark_submitted.value);
        data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value);
      } else {
        data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.submitted.value);
        data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_director.value);
      }
      try {
        let response;
        if (editData) {
          response = await updateLegalTranslation(editData.id, data);
        } else {
          response = await createLegalTranslation(data);
        }
        if (response.id) navigate(`/legal-translation/internal/${response.id}`);
        showToast(t("LegalTranslationRequestSubmittedSuccess") || "Legal Translation request submitted successfully!", "success");
        if (fileInputRefTranslationDoc.current) fileInputRefTranslationDoc.current.value = "";
      } catch (error) {
        console.error("Error submitting request:", error);
        showToast(t("FailedToSubmitLegalTranslationRequest") || "Failed to submit Legal Translation request.", "error");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    
    // External mode submission
    for (const key in formState) {
      const value = formState[key as keyof typeof formState];
      if (value !== null) {
        data.append(key, value instanceof File ? value : String(value));
      }
    }
    data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.submitted.value);
    try {
      let response;
      if (editData) {
        response = await updateLegalTranslation(editData.id, data);
      } else {
        const decoded = jwtDecode<TokenPayload>(
          contextData.user.access_token || ""
        );
        data.append(
          "requested_by",
          contextData.user?.user_type === "external"
            ? "Stakeholder"
            : contextData.user?.user_type
        );
        data.append("requested_by_id", contextData.user?.uu_id ?? "");
        data.append("stakeholder_id", contextData.user?.org_unit_id ?? "");
        data.append("first_name", decoded?.first_name || "");
        data.append("last_name", decoded?.last_name || "");
        data.append("email_user_organization", decoded?.email || "");
        data.append(
          "organization_kebele",
          decoded?.organization?.address?.kebele || ""
        );
        data.append(
          "organization_zone",
          decoded?.organization?.address?.zone || ""
        );
        data.append(
          "organization_state",
          decoded?.organization?.address?.state || ""
        );
        data.append("organization_name", decoded?.organization?.name || "");
        data.append("organization_type", decoded?.organization?.type || "");
        response = await createLegalTranslation(data);
      }
      if (response.id) navigate(`/legal-translation/${response.id}`);
      showToast(t("LegalTranslationRequestSubmittedSuccess") || "Legal Translation request submitted successfully!", "success");
      if (fileInputRefTranslationDoc.current) fileInputRefTranslationDoc.current.value = "";
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToSubmitLegalTranslationRequest") || "Failed to submit Legal Translation request.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageTitle
        icon="⚖"
        text={t("LegalTranslationRequest") || "Legal Translation Request"}
        subtitle={t("LegalTranslationSubtitle") || "Provide the necessary documents and information to request a professional legal translation."}
        iconColor="text-purple-600"
        textColor="text-[#432101]"
      />
      <form onSubmit={handleSubmit}>
        <TwoColumnLayout
          left={
            <div className="py-[22px] px-[24px] bg-white rounded-xl space-y-6">
              {isInternalMode ? (
                <>
                  <FileUpload
                    label={t("SupportingDocumentsForTranslationRequired") || "Supporting Documents for Translation*"}
                    onChange={(file: File | null) => {
                      handleFileChange(file, "translation_request_document");
                      clearError("translation_request_document");
                    }}
                    error={errors.translation_request_document}
                  />
                  <DynamicFormInput
                    inputs={[
                      {
                        value: formState.request_letter_reference_number,
                        type: "text",
                        label: t("RequestLetterReferenceNumber") || "Request Letter Reference Number",
                        name: "request_letter_reference_number",
                        onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                          handleChange(e);
                          clearError("request_letter_reference_number");
                        },
                        placeholder: t("EnterReferenceNumberPlaceholder") || "Enter reference number (e.g., #12345)",
                        error: errors.request_letter_reference_number,
                      },
                      {
                        value: formState.request_summary,
                        type: "textarea",
                        label: t("SummaryOfTranslationRequest") || "Summary of Translation Request",
                        name: "request_summary",
                        onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                          handleChange(e);
                          clearError("request_summary");
                        },
                        placeholder: t("SummaryOfTranslationRequestPlaceholder") || "Provide a concise description of the documents and purpose of the translation...",
                        error: errors.request_summary,
                      },
                      {
                        value: formState.applied_date,
                        type: "text",
                        label: t("AppliedDate") || "Applied Date",
                        name: "applied_date",
                        onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                          handleChange(e);
                          clearError("applied_date");
                        },
                        placeholder: "YYYY-MM-DD",
                        error: errors.applied_date,
                      },
                    ]}
                  />
                  <div className="flex justify-between space-x-4">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (fileInputRefTranslationDoc.current) fileInputRefTranslationDoc.current.value = "";
                        navigate(-1);
                      }}
                    >
                      {t("Cancel") || "Cancel"}
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      onClick={() => {}}
                    >
                      {submitting ? (
                        <div className="flex items-center">
                          {LoadingIcon} {t("Submitting") || "Submitting..."}
                        </div>
                      ) : (
                        t("SubmitRequest") || "Submit Request"
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid gap-6 md:grid-cols-2">
                    <FileUpload
                      label={t("OfficialRequestLetter") || "Official Request Letter"}
                      onChange={(file: File | null) => {
                        setFormState(prev => ({ ...prev, request_letter_files: file }));
                        clearError("request_letter_files");
                      }}
                      error={errors.request_letter_files}
                    />
                    <FileUpload
                      label={t("SupportingDocumentsForTranslationRequired") || "Supporting Documents for Translation*"}
                      onChange={(file: File | null) => {
                        setFormState(prev => ({ ...prev, request_documents: file }));
                        clearError("request_documents");
                      }}
                      error={errors.request_documents}
                    />
                  </div>
                  <DynamicFormInput
                    inputs={[
                      {
                        value: formState.request_letter_reference_number,
                        type: "text",
                        label: t("RequestLetterReferenceNumber") || "Request Letter Reference Number",
                        name: "request_letter_reference_number",
                        onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                          handleChange(e);
                          clearError("request_letter_reference_number");
                        },
                        placeholder: t("EnterReferenceNumberPlaceholder") || "Enter reference number (e.g., #12345)",
                        error: errors.request_letter_reference_number,
                      },
                      {
                        value: formState.request_summary,
                        type: "textarea",
                        label: t("SummaryOfTranslationRequest") || "Summary of Translation Request",
                        name: "request_summary",
                        onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                          handleChange(e);
                          clearError("request_summary");
                        },
                        placeholder: t("SummaryOfTranslationRequestPlaceholder") || "Provide a concise description of the documents and purpose of the translation...",
                        error: errors.request_summary,
                      },
                    ]}
                  />
                  <div className="flex justify-between space-x-4">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (fileInputRefTranslationDoc.current) fileInputRefTranslationDoc.current.value = "";
                        navigate(-1);
                      }}
                    >
                      {t("Cancel") || "Cancel"}
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      onClick={() => {}}
                    >
                      {submitting ? (
                        <div className="flex items-center">
                          {LoadingIcon} {t("Submitting") || "Submitting..."}
                        </div>
                      ) : (
                        t("SubmitRequest") || "Submit Request"
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          }
          right={
            <div className="w-full max-w-md space-y-4">
              <Notes
                title={t("ImportantNotes") || "Important Notes"}
                text={t("ImportantNotesText") || "Ensure all required documents are properly attached before submitting."}
                subtext={t("ImportantNotesSubtext") || "Incomplete submissions may result in processing delays or rejection."}
              />
              <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
                <h2 className="font-semibold">{t("DocumentRequirements") || "Document Requirements"}</h2>
                <p className="text-sm text-gray-500">
                  {t("DocumentRequirementsDescription") || "Provide official documents that require legal translation. Ensure clarity, completeness, and authenticity."}
                </p>
                <div className="border-t pt-2 space-y-2">
                  <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                    <span className="text-sm font-medium">
                      {t("CertifiedLegalDocuments") || "Certified Legal Documents"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                    <span className="text-sm font-medium">
                      {t("ContractsAgreementsLegalNotices") || "Contracts, Agreements, and Legal Notices"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                    <span className="text-sm font-medium">
                      {t("LegislativeDraftsPolicyDocuments") || "Legislative Drafts and Policy Documents"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </form>
    </div>
  );
};

export default CreateLegalTranslation;